/**
 * setLang — universal lang change primitive.
 *
 * Updates DOM attributes, evicts stale fetch cache, reloads all known
 * i18n namespaces, updates smbls polyglot, persists to localStorage,
 * and optionally persists to the user's profile via SDK.
 */

import { loadTranslations, setLang as polyglotSetLang } from 'smbls'
import { queryClient } from '@symbo.ls/fetch'
import { loadI18nNamespace, dropNonEnglishCache, getLoadedNamespaces, mergeAllNamespaces } from './i18nLoader.js'

const RTL_LANGS = new Set(['ar', 'he', 'fa', 'ur'])

/**
 * @param {string} newLang     - target ISO language code
 * @param {object} [opts]
 * @param {object} [opts.context]       - DOMQL context (must have .state + optional .sdk)
 * @param {boolean} [opts.skipPersist]  - skip SDK profile update
 */
export const setLang = async (newLang, opts = {}) => {
  const { sdk, shellState } = opts.context || {}

  document.documentElement.setAttribute('lang', newLang)
  document.documentElement.setAttribute('dir', RTL_LANGS.has(newLang) ? 'rtl' : 'ltr')

  dropNonEnglishCache()
  for (const ns of getLoadedNamespaces()) await loadI18nNamespace(newLang, ns)
  loadTranslations(newLang, mergeAllNamespaces(newLang))
  polyglotSetLang(newLang)

  if (shellState) shellState.lang = newLang
  localStorage.setItem('lang', newLang)

  queryClient.dropNonLang('en')

  if (!opts.skipPersist && sdk && shellState?.user) {
    // SDK proxies AuthService methods flat (sdk.updateMe). There is no
    // sdk.auth namespace — see sdk/src/utils/services.js SERVICE_METHODS.
    if (typeof sdk.updateMe === 'function') {
      await sdk.updateMe({ preferences: { lang: newLang } })
    }
  }
}
